import Avatar from "@galaxy-io/dls/avatar/Avatar";
import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Chip, { ChipSize } from "@galaxy-io/dls/chips/Chip";
import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import EmptyState from "@galaxy-io/dls/feedback/EmptyState";
import TextAreaInput from "@galaxy-io/dls/inputs/TextAreaInput";
import Flex, {
  AlignItems,
  FlexDirection,
  FlexWrap,
  JustifyContent,
} from "@galaxy-io/dls/layout/Flex";
import { HAIRLINE_WIDTH } from "@galaxy-io/dls/styles/mixins";
import Text, { TextSize, TextVariant, TextWeight } from "@galaxy-io/dls/text/Text";
import { t } from "@galaxy-io/dls/theme/tokens/t";
import Widget, { WidgetSize, WidgetVariant } from "@galaxy-io/dls/widget/Widget";
import { type DreamMap, type DreamNode, dreamMapInput } from "@gtm/contracts/core/dream-list";
import { styled } from "@linaria/react";
import {
  ArrowLeftIcon,
  ArrowsOutIcon,
  MinusIcon,
  PlusIcon,
  TrashIcon,
  TreeStructureIcon,
} from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  applyNodeChanges,
  Background,
  Handle,
  type Node,
  type NodeProps,
  Panel,
  Position,
  ReactFlow,
  type ReactFlowInstance,
  useNodesState,
} from "@xyflow/react";
import "@xyflow/react/dist/base.css";
import { useCallback, useEffect, useRef, useState } from "react";
import { QueryState } from "@/components/query-state";
import { Button } from "@/components/ui/button";
import { ConfirmAction } from "@/components/workspace/confirm-action";
import { PageHeader } from "@/components/workspace/page-header";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { BaseToolbar } from "@/components/workspace/toolbar";
import { apiClient, unwrap } from "@/lib/api-client";
import { arrangeOrgChart } from "./layout";
import { NodeEditor } from "./node-editor";

const Canvas = styled.div`
  position: relative;
  flex: 1;
  min-height: 440px;
  min-width: 0;
  background: ${t.color.background.base};
  .react-flow__edge-path { stroke: ${t.color.border.secondary}; stroke-width: 2; }
  .react-flow__handle { width: 10px; height: 10px; background: ${t.color.text.secondary}; border: 2px solid ${t.color.background.base}; }
  .react-flow__attribution { background: ${t.color.background.base}; color: ${t.color.text.secondary}; }
  .react-flow__attribution a { color: inherit; }
`;
const Card = styled.div`
  width: 260px;
  & [data-placeholder="true"] { border-style: dashed; }
`;
const Status = styled.div`
  padding: ${t.space[12]} ${t.space[16]};
  border-bottom: ${HAIRLINE_WIDTH} solid ${t.color.border.primary};
`;
type OrgNode = Node<
  { person: DreamNode; edit: () => void; addReport: () => void; busy: boolean },
  "person"
>;
function PersonCard({ data }: NodeProps<OrgNode>) {
  const node = data.person;
  return (
    <Card>
      <Handle type="target" position={Position.Top} aria-label="Reports to" />
      <Widget
        size={WidgetSize.LARGE}
        data-placeholder={!node.personId}
        variant={node.personId ? WidgetVariant.PRIMARY : WidgetVariant.SECONDARY}
        footer={
          <Flex gap={8} width="100%" justifyContent={JustifyContent.SPACE_BETWEEN}>
            <Button
              label="Edit"
              size={ButtonSize.SMALL}
              variant={ButtonVariant.TERTIARY}
              className="nodrag"
              isDisabled={data.busy}
              onClick={data.edit}
            />
            <Button
              label="Add report"
              icon={PlusIcon}
              size={ButtonSize.SMALL}
              variant={ButtonVariant.TERTIARY}
              className="nodrag"
              isDisabled={data.busy}
              onClick={data.addReport}
            />
          </Flex>
        }
      >
        <Flex direction={FlexDirection.COLUMN} gap={12}>
          <Flex gap={12} alignItems={AlignItems.CENTER}>
            <Avatar name={node.name || undefined} seed={node.personId ? undefined : node.id} />
            <Flex direction={FlexDirection.COLUMN} gap={4} minWidth={0}>
              <Text
                size={TextSize.BODY_MD}
                weight={TextWeight.MEDIUM}
                lineClamp={2}
                shouldTooltipOnOverflow
              >
                {node.personId ? node.name || "Person unavailable" : node.role}
              </Text>
              <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY} lineClamp={2}>
                {node.personId ? node.role : "Person to identify"}
              </Text>
            </Flex>
          </Flex>
          {(node.department || !node.personId) && (
            <Flex gap={8} wrap={FlexWrap.WRAP} alignItems={AlignItems.CENTER}>
              {!node.personId && (
                <Chip
                  size={ChipSize.SMALL}
                  label="Role to find"
                  tooltip="Add a person when you know who holds this role"
                />
              )}
              {node.department && (
                <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
                  {node.department}
                </Text>
              )}
            </Flex>
          )}
          {node.notes && (
            <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY} lineClamp={2}>
              {node.notes}
            </Text>
          )}
        </Flex>
      </Widget>
      <Handle type="source" position={Position.Bottom} aria-label="Direct reports" />
    </Card>
  );
}
const nodeTypes = { person: PersonCard };
export function DreamMapView({ id, onBack }: { id: string; onBack: () => void }) {
  const query = useQuery({
    queryKey: ["dream-map", id],
    queryFn: ({ signal }) =>
      unwrap(apiClient().GET("/dream-list/{id}", { params: { path: { id } }, signal })),
    retry: false,
  });
  if (!query.data)
    return (
      <>
        <PageHeader
          title="Company map"
          actions={<Button label="Dream List" onClick={onBack} variant={ButtonVariant.SECONDARY} />}
        />
        <QueryState error={query.error} onRetry={() => void query.refetch()} />
      </>
    );
  return <MapEditor initial={query.data} onBack={onBack} />;
}
function MapEditor({ initial, onBack }: { initial: DreamMap; onBack: () => void }) {
  const client = useQueryClient();
  const [map, setMap] = useState(initial);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [error, setError] = useState<string>();
  const [unsaved, setUnsaved] = useState(false);
  const [editing, setEditing] = useState<DreamNode | null>(null);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [removing, setRemoving] = useState(false);
  const [discard, setDiscard] = useState<"back" | "reload" | null>(null);
  const flow = useRef<ReactFlowInstance<OrgNode> | null>(null);
  useEffect(() => {
    if (!unsaved && !busy) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [unsaved, busy]);
  async function persist(next: DreamMap) {
    if (busyRef.current) throw new Error("Wait for the current save to finish.");
    const input = dreamMapInput.parse(next);
    busyRef.current = true;
    setBusy(true);
    setError(undefined);
    try {
      const saved = await unwrap(
        apiClient().PUT("/dream-list/{id}", { params: { path: { id: map.id } }, body: input }),
      );
      setMap(saved);
      setUnsaved(false);
      client.setQueryData(["dream-map", map.id], saved);
      void client.invalidateQueries({ queryKey: ["dream-list"] });
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }
  function saveCanvas(next: DreamMap) {
    setMap(next);
    setUnsaved(true);
    void persist(next).catch((reason) => setError((reason as Error).message));
  }
  const add = useCallback(
    (reportsTo: string | null = null) => {
      const manager = map.nodes.find((node) => node.id === reportsTo);
      setEditing({
        id: crypto.randomUUID(),
        personId: null,
        name: null,
        email: null,
        role: "",
        department: manager?.department || "",
        notes: "",
        reportsTo,
        x: manager
          ? manager.x + map.nodes.filter((node) => node.reportsTo === reportsTo).length * 300
          : map.nodes.length * 300,
        y: manager ? manager.y + 240 : 0,
      });
    },
    [map.nodes],
  );
  const [nodes, setNodes, onNodesChange] = useNodesState<OrgNode>([]);
  useEffect(() => {
    setNodes((current) =>
      map.nodes.map((person) => ({
        ...current.find((node) => node.id === person.id),
        id: person.id,
        type: "person",
        position: { x: person.x, y: person.y },
        ariaLabel: `${person.name || "Role to find"}, ${person.role}`,
        data: {
          person,
          edit: () => setEditing(person),
          addReport: () => add(person.id),
          busy: busy || unsaved,
        },
      })),
    );
  }, [map, busy, unsaved, setNodes, add]);
  const peopleCount = map.nodes.filter((node) => node.personId).length;
  const roleCount = map.nodes.length - peopleCount;
  const edges = map.nodes.flatMap((node) =>
    node.reportsTo
      ? [{ id: node.id, source: node.reportsTo, target: node.id, type: "smoothstep" }]
      : [],
  );
  return (
    <>
      <PageHeader
        title={map.name}
        description={map.domain || "Dream company"}
        actions={
          <Flex gap={8} wrap={FlexWrap.WRAP}>
            <Button
              label="Dream List"
              icon={ArrowLeftIcon}
              size={ButtonSize.SMALL}
              variant={ButtonVariant.SECONDARY}
              isDisabled={busy}
              onClick={() => (unsaved ? setDiscard("back") : onBack())}
            />
            <Button
              label="Add person or role"
              icon={PlusIcon}
              size={ButtonSize.SMALL}
              isDisabled={busy || unsaved}
              onClick={() => add()}
            />
          </Flex>
        }
      />
      <BaseToolbar
        leadingActions={
          <Flex gap={8} wrap={FlexWrap.WRAP} alignItems={AlignItems.CENTER}>
            <Button
              label="Arrange"
              icon={TreeStructureIcon}
              size={ButtonSize.SMALL}
              variant={ButtonVariant.SECONDARY}
              isDisabled={busy || unsaved || !nodes.length}
              onClick={() => {
                const next = { ...map, nodes: arrangeOrgChart(map.nodes) };
                saveCanvas(next);
                requestAnimationFrame(
                  () => void flow.current?.fitView({ padding: 0.2, maxZoom: 1 }),
                );
              }}
            />
            <Button
              label="Company notes"
              size={ButtonSize.SMALL}
              variant={ButtonVariant.TERTIARY}
              isDisabled={busy || unsaved}
              onClick={() => {
                setNotes(map.notes);
                setNotesOpen(true);
              }}
            />
          </Flex>
        }
        trailingActions={
          <Button
            ariaLabel="Remove company from Dream List"
            tooltip="Remove company from Dream List"
            icon={TrashIcon}
            size={ButtonSize.SMALL}
            variant={ButtonVariant.TERTIARY}
            isDisabled={busy || unsaved}
            onClick={() => setRemoving(true)}
          />
        }
      />
      <Status>
        <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
          {busy ? "Saving map…" : unsaved ? "Changes not saved" : "All changes saved"} ·{" "}
          {peopleCount} {peopleCount === 1 ? "person" : "people"} · {roleCount}{" "}
          {roleCount === 1 ? "role" : "roles"} to find
        </Text>
      </Status>
      {error && (
        <Status>
          <Flex direction={FlexDirection.COLUMN} gap={8}>
            <Alert variant={AlertVariant.ERROR}>{error}</Alert>
            {unsaved ? (
              <Flex gap={8}>
                <Button
                  label="Retry save"
                  size={ButtonSize.SMALL}
                  isLoading={busy}
                  onClick={() => saveCanvas(map)}
                />
                <Button
                  label="Reload latest map"
                  size={ButtonSize.SMALL}
                  variant={ButtonVariant.SECONDARY}
                  onClick={() => setDiscard("reload")}
                />
              </Flex>
            ) : (
              <Button
                label="Dismiss"
                size={ButtonSize.SMALL}
                variant={ButtonVariant.TERTIARY}
                onClick={() => setError(undefined)}
              />
            )}
          </Flex>
        </Status>
      )}
      {!map.nodes.length ? (
        <EmptyState
          icon={TreeStructureIcon}
          header="Who belongs in the conversation?"
          description="Start with someone you know, or a role you want to find. Connect their reporting lines as the picture comes together."
          actions={
            <Button
              variant={ButtonVariant.SECONDARY}
              label="Add the first position"
              onClick={() => add()}
            />
          }
        />
      ) : (
        <Canvas aria-label="Company organization chart">
          <ReactFlow<OrgNode>
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
            onInit={(instance) => {
              flow.current = instance;
            }}
            nodesDraggable={!busy && !unsaved}
            nodesConnectable={!busy && !unsaved}
            deleteKeyCode={null}
            onNodesChange={(changes) => {
              onNodesChange(changes);
              // React Flow moves focused nodes with arrow keys without a drag gesture.
              if (
                !busyRef.current &&
                changes.some(
                  (change) =>
                    change.type === "position" && change.position && change.dragging === undefined,
                )
              ) {
                const moved = applyNodeChanges(changes, nodes);
                saveCanvas({
                  ...map,
                  nodes: map.nodes.map((person) => {
                    const node = moved.find((node) => node.id === person.id);
                    return node ? { ...person, ...node.position } : person;
                  }),
                });
              }
            }}
            onNodeDragStop={(_, node) =>
              saveCanvas({
                ...map,
                nodes: map.nodes.map((n) => (n.id === node.id ? { ...n, ...node.position } : n)),
              })
            }
            onConnect={({ source, target }) => {
              const next = {
                ...map,
                nodes: map.nodes.map((node) =>
                  node.id === target ? { ...node, reportsTo: source } : node,
                ),
              };
              const parsed = dreamMapInput.safeParse(next);
              if (!parsed.success) {
                setError(parsed.error.issues[0]?.message);
                return;
              }
              saveCanvas(next);
            }}
          >
            <Panel position="bottom-right">
              <Widget size={WidgetSize.SMALL}>
                <Flex gap={4} alignItems={AlignItems.CENTER}>
                  <Button
                    label="Fit map"
                    icon={ArrowsOutIcon}
                    size={ButtonSize.SMALL}
                    variant={ButtonVariant.TERTIARY}
                    onClick={() => void flow.current?.fitView({ padding: 0.2, maxZoom: 1 })}
                  />
                  <Button
                    ariaLabel="Zoom in"
                    tooltip="Zoom in"
                    icon={PlusIcon}
                    size={ButtonSize.SMALL}
                    variant={ButtonVariant.TERTIARY}
                    onClick={() => void flow.current?.zoomIn()}
                  />
                  <Button
                    ariaLabel="Zoom out"
                    tooltip="Zoom out"
                    icon={MinusIcon}
                    size={ButtonSize.SMALL}
                    variant={ButtonVariant.TERTIARY}
                    onClick={() => void flow.current?.zoomOut()}
                  />
                </Flex>
              </Widget>
            </Panel>
            <Background color={t.color.border.primary} gap={24} />
          </ReactFlow>
        </Canvas>
      )}
      <Status>
        <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
          Drag cards to arrange. Connect a manager’s lower handle to a report’s upper handle, or use
          “Reports to” in Edit.
        </Text>
      </Status>
      {editing && (
        <NodeEditor
          key={editing.id}
          map={map}
          node={editing}
          onClose={() => setEditing(null)}
          onSave={(node) =>
            persist({ ...map, nodes: [...map.nodes.filter((n) => n.id !== node.id), node] })
          }
          onRemove={
            map.nodes.some((node) => node.id === editing.id)
              ? () =>
                  persist({
                    ...map,
                    nodes: map.nodes
                      .filter((node) => node.id !== editing.id)
                      .map((node) =>
                        node.reportsTo === editing.id ? { ...node, reportsTo: null } : node,
                      ),
                  })
              : undefined
          }
        />
      )}
      {notesOpen && (
        <SurfaceDialog
          open
          onClose={() => setNotesOpen(false)}
          title="Company notes"
          dirty={notes !== map.notes}
          busy={busy}
          footer={
            <Button
              label="Save notes"
              isLoading={busy}
              onClick={() =>
                void persist({ ...map, notes })
                  .then(() => setNotesOpen(false))
                  .catch((reason) => setError((reason as Error).message))
              }
            />
          }
        >
          <TextAreaInput
            label="Why this company?"
            value={notes}
            onChange={setNotes}
            placeholder="What makes this a dream account? Where could you help?"
          />
          {error && <Alert variant={AlertVariant.ERROR}>{error}</Alert>}
        </SurfaceDialog>
      )}
      <ConfirmAction
        isOpen={removing}
        onOpenChange={setRemoving}
        header="Remove dream company"
        label="Remove from Dream List"
        isDestructive
        description="Remove this company and its map from Dream List? The company and people remain in your directory."
        onConfirm={async () => {
          await unwrap(
            apiClient().DELETE("/dream-list/{id}", {
              params: { path: { id: map.id } },
              body: { revision: map.revision },
            }),
          );
          void client.invalidateQueries({ queryKey: ["dream-list"] });
          client.removeQueries({ queryKey: ["dream-map", map.id] });
          onBack();
        }}
      />
      <ConfirmAction
        isOpen={!!discard}
        onOpenChange={(open) => {
          if (!open) setDiscard(null);
        }}
        header="Discard unsaved map changes?"
        label={discard === "back" ? "Discard and go back" : "Reload latest map"}
        description="Your unsaved positions will be replaced with the latest saved map."
        onConfirm={async () => {
          if (discard === "back") {
            setDiscard(null);
            onBack();
            return;
          }
          const latest = await unwrap(
            apiClient().GET("/dream-list/{id}", { params: { path: { id: map.id } } }),
          );
          setMap(latest);
          setUnsaved(false);
          setError(undefined);
          setDiscard(null);
        }}
      />
    </>
  );
}
